export const VISITOR_THEME_STORAGE_KEY = 'zgharta-theme';
export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

// The visitor app's colour register. `light` is what src/App.js, src/index.css and
// public/index.html paint TODAY, value for value — #422 is an indirection with no visible change,
// so a light value here is a transcription, never a decision. `dark` carries the #421 D3 starting
// values; nothing mounts it yet (`useVisitorTheme` is written and tested but not called by
// src/App.js until #423), and its text pairs are already asserted for WCAG AA by
// visitorTheme.test.js so that #423 tunes surfaces rather than legibility.
//
// Naming: semantic keys where a colour has one job (`ink`, `paper`, `accent`); a family + opacity
// suffix where a value is one step on a ramp of the same ink (`scrim15`, `glass86`, `warm12`).
// Two keys may share a light value on purpose when they part company in dark — `onPhoto` stays
// white over a photograph while `rootBg` goes dark, and `markerIconOnFav` keeps its light value
// while `glass90` does not.
//
// Do NOT import from `src/admin/` here: nothing under that directory may be reachable from the
// visitor bundle (the lazy-load boundary, #310), and src/admin/adminTheme.test.js asserts it.
export const VISITOR_PALETTES = {
  light: {
    // --- brand -------------------------------------------------------------------------------
    accent: '#0F5132',
    accentDeep: '#0F5132',
    accentBright: '#047857',
    accentSoft: 'rgba(15,81,50,0.08)',
    // A hex-8 rather than an rgba on purpose: src/App.js used to build this border by appending
    // an alpha pair to the accent hex (`${premiumTheme.colors.accent}22`), which stops working
    // the moment the accent is a `var()` — `var(--visitor-accent)22` is not a colour. The same
    // trick over a category hue is untouched, because those stay literals.
    accentHairline: '#0F513222',
    accentTint: '#ecfdf5',
    accentTintStrong: '#d1fae5',
    onAccentSoft: '#a7f3d0',
    mapAccent: '#10b981',
    amber: '#d4a054',
    star: '#fbbf24',

    // --- surfaces ----------------------------------------------------------------------------
    appBg: '#FAF7F2',
    appWarm: '#FAF7F2',
    paper: '#FAF7F2',
    surface: '#FFFDF8',
    surfaceGlass: '#FFFDF8',
    modalBg: '#fbfaf7',
    canvas: '#f9fafb',
    rootBg: 'white',
    panelSolid: 'white',
    fallbackWash: '#fffefa',
    fallbackScrim: 'transparent',
    warmTrack: '#f7f4ed',
    neutralSurface: '#f3f4f6',
    ghostSurface: '#f8fafc',
    mutedSurface: '#EFE8DC',
    pastSurface: '#f5f5f4',

    // --- lines -------------------------------------------------------------------------------
    hairline: '#E6DED2',
    warmLine: 'rgba(180,148,92,0.16)',
    checkboxLine: '#d1d5db',
    emptyIcon: '#d1d5db',

    // --- ink ---------------------------------------------------------------------------------
    ink: '#1A1A1A',
    mutedInk: '#6B6B6B',
    title: '#1f2937',
    chipInk: '#374151',
    controlInk: '#4b5563',
    meta: '#6b7280',
    faintInk: '#6B6B6B',
    warmMeta: '#786f62',
    year: '#6B6B6B',
    pastInk: '#5f574d',
    pastMeta: '#6B6B6B',
    pastFaint: '#6B6B6B',
    chevronInk: '#9B9388',
    // Three "on-" tokens, because they part company in dark: `onPhoto` is text and icons laid
    // over photography (white in both modes, because the scrim under it darkens instead);
    // `onAccent` is text on an accent-filled button or badge, and `onInk` is text on an
    // ink-filled control — both of which invert to a dark ink once the fill itself goes light.
    onPhoto: 'white',
    onAccent: 'white',
    onAccentBody: 'rgba(255,255,255,0.78)',
    onAccentFaint: 'rgba(255,255,255,0.72)',
    onInk: 'white',
    // The four decorative washes inside the Saved folio hero, which is painted with the ACCENT
    // fill. They read a white-alpha ramp in light because the fill is dark there; in dark the
    // fill is a light green, so a white wash would erase the tile and leave the dark ink on it
    // at 2.87:1. They therefore invert with the fill, exactly as `onAccent` does, and are their
    // own tokens rather than shared `glass*` steps because those steps also paint frosted
    // surfaces over photography, which do NOT invert. Light values are byte-identical to the
    // `glass13 / glass18 / glass12 / glass14` they replace (#422 leftover, fixed in #423).
    onAccentTile: 'rgba(255,255,255,0.13)',
    onAccentTileLine: 'rgba(255,255,255,0.18)',
    onAccentPanel: 'rgba(255,255,255,0.12)',
    onAccentPanelLine: 'rgba(255,255,255,0.14)',
    // The two frosted plates that sit OVER a photograph and carry a CATEGORY HUE as their text:
    // the Saved card's stop number, and the nearby strip's category pill. A category hue is not
    // theme surface — the register in src/config/categories.js is tuned for a light ground and
    // #421 leaves it alone — so these stay WHITE in dark rather than following `glass82` /
    // `glass92`, which invert to near-black plates and took the restaurant hue to 3.66:1. Dark
    // carries MORE opacity than light, because what shows through in dark is the darker photo
    // fallback: at light's 0.82 the same hue still only reached 4.27:1. The suffix is the LIGHT
    // alpha, and each light value is byte-identical to the `glass` step it replaces (#423).
    photoPlate82: 'rgba(255,255,255,0.82)',
    photoPlate92: 'rgba(255,255,255,0.92)',

    // --- map markers (values, never var(): SVG presentation attributes and Maps API options) ---
    markerHalo: 'white',
    markerBg: 'white',
    markerIconOnFav: 'rgba(255,255,255,0.9)',

    // --- status ------------------------------------------------------------------------------
    errorBg: '#f9fafb',
    // Raised from #ef4444 in #423. That value put the connection-error heading at 3.60:1 on its
    // own background — the one AA shortfall #422 pinned rather than fixed, because #422 was not
    // allowed to change a light value. #dc2626 is the closest red to it that clears AA on both
    // grounds this token paints on: 4.62:1 on `errorBg`, 4.83:1 on `panelSolid` (the village
    // filter's "Clear All"). Neither surface is on a diffed screen, so light rendering of the
    // five tabs and the detail modal is still byte-identical.
    errorInk: '#dc2626',
    errorSoftBg: '#fef2f2',
    errorSoftLine: '#fecaca',
    errorSoftInk: '#991b1b',
    removeBg: '#fff7f7',
    removeLine: 'rgba(239,68,68,0.14)',
    removeInk: '#b91c1c',
    clearFilterBg: '#fee2e2',
    clearFilterInk: '#dc2626',
    warnInk: '#b45309',
    warnSoftBg: '#fffbeb',
    warnSoftLine: '#fde68a',
    warnSoftInk: '#854d0e',
    noticeSoftBg: '#fff7ed',
    noticeSoftLine: '#fed7aa',
    noticeSoftInk: '#9a3412',

    // --- map ---------------------------------------------------------------------------------
    mapLoadingBg: '#f0f4ee',
    mapPlaceholderBg: '#e8f5e9',
    mapPlaceholderInk: '#86efac',
    mapControlLine: 'rgba(16,185,129,0.25)',
    mapPillLine: 'rgba(16,185,129,0.3)',
    geoDot: '#3b82f6',
    geoActiveInk: '#2563eb',
    geoHalo: 'rgba(59,130,246,0.5)',
    geoPulse: 'rgba(59,130,246,0.4)',
    geoPulseFade: 'rgba(59,130,246,0)',
    geoActiveBg: 'rgba(219,234,254,0.72)',
    homePulse: 'rgba(16,185,129,0.4)',
    homePulseFade: 'rgba(16,185,129,0)',

    // --- map chrome (#424) --------------------------------------------------------------------
    // The frosted search plate, filter chips, locate-me / carousel buttons and return pill float
    // OVER the raster map, so their backdrop is the MAP, not an app surface. That is why they are
    // their own ramp rather than the `glass*` steps they read until #424: the dark `glass*` values
    // were tuned for indoor surfaces and photography, and `glass38`'s dark
    // `rgba(28,24,20,0.72)` composited over the dark map's own land colour lands within 2/255 per
    // channel of that land — a chip that is, in paint, invisible. The dark values here go the
    // other way: DARKER than the map (so the plate reads as a plate) with a light hairline (so
    // its edge reads at all). Every LIGHT value is byte-identical to the `glass*` step it
    // replaced, so the map tab paints exactly as it did on main.
    //
    // The suffix is the LIGHT alpha, following the `glass*` and `photoPlate*` convention.
    // `mapGlass28` and `mapGlassLine` share a light value and part company in dark, because one
    // is a plate and the other is the line around it.
    mapGlass24: 'rgba(255,255,255,0.24)',
    mapGlass28: 'rgba(255,255,255,0.28)',
    mapGlass38: 'rgba(255,255,255,0.38)',
    mapGlass48: 'rgba(255,255,255,0.48)',
    mapGlass52: 'rgba(255,255,255,0.52)',
    mapGlassLine: 'rgba(255,255,255,0.28)',
    mapChipLine: 'rgba(0,0,0,0.1)',
    mapNavBg: 'rgba(255,255,255,0.14)',
    mapNavLine: 'rgba(255,255,255,0.12)',

    // --- bottom nav --------------------------------------------------------------------------
    navActiveBg: 'rgba(16,185,129,0.22)',
    navActiveLine: 'rgba(16,185,129,0.18)',
    navPastBg: 'rgba(251,146,60,0.18)',
    navPastLine: 'rgba(251,146,60,0.28)',
    navMapInk: 'rgba(15,23,42,0.72)',

    // --- links + focus -----------------------------------------------------------------------
    linkButton: '#2563eb',
    linkShadow: 'rgba(37,99,235,0.16)',
    mapsLinkBg: 'rgba(249,250,251,0.88)',
    hookWash: 'rgba(236,253,245,0.96)',
    hookLine: 'rgba(16,185,129,0.22)',
    focusRing: 'rgba(16,185,129,0.72)',

    // --- accent ramp -------------------------------------------------------------------------
    accent07: 'rgba(15,81,50,0.07)',
    accent09: 'rgba(15,81,50,0.09)',
    accent10: 'rgba(15,81,50,0.1)',
    accent14: 'rgba(15,81,50,0.14)',
    accent18: 'rgba(15,81,50,0.18)',
    accent20: 'rgba(15,81,50,0.2)',
    accent24: 'rgba(15,81,50,0.24)',
    accentBright08: 'rgba(4,120,87,0.08)',
    accentBright92: 'rgba(4,120,87,0.92)',

    // --- warm-stone ramp (chip borders, filter tracks) ----------------------------------------
    warm10: 'rgba(120,113,108,0.1)',
    warm12: 'rgba(120,113,108,0.12)',
    warm13: 'rgba(120,113,108,0.13)',
    warm14: 'rgba(120,113,108,0.14)',

    // --- slate shade ramp (card shadows) ------------------------------------------------------
    shade06: 'rgba(31,41,55,0.06)',
    shade08: 'rgba(31,41,55,0.08)',
    shade09: 'rgba(31,41,55,0.09)',
    shade10: 'rgba(31,41,55,0.1)',
    shade12: 'rgba(31,41,55,0.12)',
    glassActive: 'rgba(31,41,55,0.76)',

    // --- black scrim ramp ---------------------------------------------------------------------
    scrim08: 'rgba(0,0,0,0.08)',
    scrim10: 'rgba(0,0,0,0.1)',
    scrim11: 'rgba(0,0,0,0.11)',
    scrim12: 'rgba(0,0,0,0.12)',
    scrim15: 'rgba(0,0,0,0.15)',
    scrim25: 'rgba(0,0,0,0.25)',
    scrim30: 'rgba(0,0,0,0.3)',
    scrim70: 'rgba(0,0,0,0.7)',

    // --- photo hero scrims --------------------------------------------------------------------
    heroScrim06: 'rgba(17,24,39,0.06)',
    heroScrim30: 'rgba(17,24,39,0.3)',
    heroScrim62: 'rgba(17,24,39,0.62)',
    photoWash: 'rgba(17,24,39,0.72)',

    // --- frosted white ramp (chips over photography, plates on an app surface) -----------------
    // The map controls left this ramp in #424 for the `mapGlass*` ramp above. Eight steps are
    // therefore no longer read at runtime — `glass12`, `glass14`, `glass24`, `glass28`, `glass38`,
    // `glass48`, `glass52` and `scrim10` — and they are RETAINED deliberately rather than deleted
    // (cold review, #424). They are the byte-identical light baseline the `mapGlass*` / `onAccent*`
    // tokens are asserted against by visitorTheme.test.js, which is what proves the Map tab and
    // the Saved hero did not move in light mode. Deleting them would replace those assertions with
    // bare literals and lose the statement of WHICH value each token had to keep.
    // What stops them being misused again is a test, not their absence: `no map surface reads the
    // indoor frosted ramp` in visitorTheme.test.js fails if a map plate reads a `glass*` step —
    // the exact defect the `mapGlass*` ramp exists to fix, since these dark values composite to
    // within a couple of levels of the dark map's own land colour.
    glass12: 'rgba(255,255,255,0.12)',
    glass13: 'rgba(255,255,255,0.13)',
    glass14: 'rgba(255,255,255,0.14)',
    glass18: 'rgba(255,255,255,0.18)',
    glass20: 'rgba(255,255,255,0.2)',
    glass24: 'rgba(255,255,255,0.24)',
    glass28: 'rgba(255,255,255,0.28)',
    glass38: 'rgba(255,255,255,0.38)',
    glass40: 'rgba(255,255,255,0.4)',
    glass48: 'rgba(255,255,255,0.48)',
    glass52: 'rgba(255,255,255,0.52)',
    glass60: 'rgba(255,255,255,0.6)',
    glass68: 'rgba(255,255,255,0.68)',
    glass72: 'rgba(255,255,255,0.72)',
    glass76: 'rgba(255,255,255,0.76)',
    glass80: 'rgba(255,255,255,0.8)',
    glass82: 'rgba(255,255,255,0.82)',
    glass86: 'rgba(255,255,255,0.86)',
    glass90: 'rgba(255,255,255,0.9)',
    glass92: 'rgba(255,255,255,0.92)',
    glass94: 'rgba(255,255,255,0.94)',
    glass95: 'rgba(255,255,255,0.95)',

    // --- category getter fallbacks (src/config/categories.js) ----------------------------------
    categoryFallbackHue: '#10b981',
    categoryFallbackBg: '#f3f4f6',
    categoryFallbackMuted: 'rgba(16,185,129,0.65)',

    // --- category ink register (#428) ---------------------------------------------------------
    // The CATEGORY_UI register in src/config/categories.js is the HUE register — marker colours,
    // card gradients and emoji — and it is tuned for a light ground. It stays exactly as it is:
    // a hue a visitor learns on the map has to be the same hue on every screen and in both modes.
    //
    // What moves per mode is the CONTRAST of the text and the badge drawn in that hue. Its badge
    // pair (`badgeColor` on `bgColor`) failed WCAG AA identically in BOTH modes, because both
    // halves are literals that never followed the theme: religious 2.86:1, nature 3.00, cafe 3.35,
    // restaurant 3.95, hotel 4.24 (#423 browser QA). This block is the fix, and it is a REGISTER
    // rather than a re-tune of the hues precisely so the map does not move.
    //
    //   `ink`      the hue as TEXT — on `chipBg`, and on `surface` / `paper` / `modalBg`.
    //   `chipBg`   the tinted pill behind that text. Every LIGHT value is byte-identical to the
    //              `bgColor` it replaces (asserted by test), so the tint itself does not move in
    //              light — only the ink on it darkens.
    //   `chipLine` the hue at 13% alpha, written out rather than concatenated, exactly as
    //              `eventCat.line` is: `var(--visitor-…)22` is not a colour.
    //   `onPlate`  the hue as text on `photoPlate82` / `photoPlate92`. Those two plates are WHITE
    //              in both modes on purpose (#423) — a translucent plate over a photograph, whose
    //              real backdrop is the picture — so their ink does not follow the palette either:
    //              dark's `onPlate` IS light's `ink`, and a test asserts that pairing.
    //
    // Owner is colourblind: nothing here is a status-by-colour cue. Every chip and badge already
    // carries its own icon and its own word, and this change moves ink only.
    categoryInk: {
      restaurant: { ink: '#b91c1c', chipBg: '#fee2e2', chipLine: '#b91c1c22', onPlate: '#b91c1c' },
      cafe: { ink: '#c2410c', chipBg: '#fff7ed', chipLine: '#c2410c22', onPlate: '#c2410c' },
      shop: { ink: '#7c3aed', chipBg: '#f3e8ff', chipLine: '#7c3aed22', onPlate: '#7c3aed' },
      heritage: { ink: '#57534e', chipBg: '#f5f5f4', chipLine: '#57534e22', onPlate: '#57534e' },
      nature: { ink: '#166534', chipBg: '#dcfce7', chipLine: '#16653422', onPlate: '#166534' },
      hotel: { ink: '#1d4ed8', chipBg: '#dbeafe', chipLine: '#1d4ed822', onPlate: '#1d4ed8' },
      religious: { ink: '#92400e', chipBg: '#fef3c7', chipLine: '#92400e22', onPlate: '#92400e' },
      health: { ink: '#0f766e', chipBg: '#ccfbf1', chipLine: '#0f766e22', onPlate: '#0f766e' },
      // The unrecognised-category entry, in the same shape. No catalog row reaches it — the
      // per-table CHECK constraint pins `category` to the eight above — but src/App.js had five
      // `|| VISITOR_PALETTES.light.accent` / `|| premiumTheme.colors.accent` fallbacks for it, and
      // a light-pinned literal is exactly what this ticket removes. It borrows the neutral caza
      // pill's tones deliberately: "we cannot attribute this row" is the same statement in paint.
      fallback: { ink: '#4B5563', chipBg: '#F1F0EE', chipLine: '#4B556322', onPlate: '#4B5563' },
    },
    // The ink on a chip FILLED with the raw category hue — the active Directory pill and the
    // active Map filter chip. It was `onAccent`, which is white in light, and white on a mid
    // pastel is 2.34:1 for religious and never better than 3.59:1 for any hue. A dark ink clears
    // AA on all eight in both modes (4.85:1 worst), and it is the same rule in both, so the
    // active chip does not change character between them.
    categoryOnFill: '#1A1A1A',

    // --- shadows (whole strings; geometry is not a theme decision) -----------------------------
    shadowAction: '0 12px 24px rgba(15,81,50,0.18)',
    shadowCard: '0 20px 46px rgba(31,41,55,0.1)',
    shadowMapControl: '0 2px 10px rgba(0,0,0,0.15)',
    shadowSoft: '0 12px 30px rgba(31,41,55,0.08)',
    shadowPrimaryBtn: '0 10px 22px rgba(15,81,50,0.16)',

    // --- events week board (#450) -------------------------------------------------------------
    // Named for the board rather than for the button ramp they happen to equal today: the Events
    // tab's today plate and the primary action button share a value in both palettes, but the
    // board has to be re-tunable without repainting every button in the app.
    eventTodayFill: '#0F5132',
    eventTodayInk: '#fff',
    // The 2px day spine. `hairline` is too faint at 2px in light — the spine is the thing that
    // holds a day group together when its cards carry no photographs.
    eventDaySpine: '#D9CFBF',
    // Unfilled night segments on a multi-night run bar; a filled segment is `accent`.
    eventRunTrack: 'rgba(15,81,50,0.22)',
    // The weekend plate on the 7-day strip. It is a SHAPE cue as much as a tone one — the day
    // buttons also carry their own count text, because the owner is colourblind and a tint alone
    // is never the only thing saying "weekend".
    eventWeekendPlate: '#F1EBE0',
    eventWeekendPlateLine: 'rgba(180,148,92,0.28)',

    // --- caza area pills (#361 Variant B, #379) -----------------------------------------------
    // Both backgrounds are OPAQUE on purpose: the pill also renders over photography, where a
    // translucent background lets the dark ink sit straight on a dark image and the label stops
    // being readable (browser QA, #372). Neutral is deliberately NOT the Zgharta tone — colouring
    // an unowned row green is the same cross-attribution claim in paint that defaulting its caza
    // would be in data (#379).
    cazaBadge: {
      zgharta: { color: '#0F5132', background: '#ECF1EF' },
      bsharri: { color: '#7A4A1E', background: '#F4E9DB' },
      neutral: { color: '#4B5563', background: '#F1F0EE' },
    },
    // Google Maps polygon options — read as VALUES, never as var(): these are Maps API options,
    // not CSS.
    cazaArea: {
      zgharta: '#0F5132',
      bsharri: '#A9722F',
    },

    // --- event categories ---------------------------------------------------------------------
    // `line` is the hue at 13% alpha, written out rather than concatenated: the event card and
    // the event modal chip built their border as `${s.color}22`, which a `var()` cannot carry.
    eventCat: {
      festival: { bg: '#f3e8ff', color: '#9333ea', line: '#9333ea22' },
      religious: { bg: '#fef3c7', color: '#d97706', line: '#d9770622' },
      nature: { bg: '#dcfce7', color: '#16a34a', line: '#16a34a22' },
      cultural: { bg: '#dbeafe', color: '#2563eb', line: '#2563eb22' },
    },
  },

  dark: {
    accent: '#5fc492',
    accentDeep: '#7fd3b0',
    accentBright: '#7fd3b0',
    accentSoft: 'rgba(95,196,146,0.14)',
    accentHairline: '#5fc49222',
    accentTint: '#102e25',
    accentTintStrong: '#123a2c',
    onAccentSoft: '#0d2a20',
    mapAccent: '#5fc492',
    amber: '#e0b06a',
    star: '#e8c46a',

    appBg: '#1a1714',
    appWarm: '#1a1714',
    paper: '#1a1714',
    surface: '#221e1a',
    surfaceGlass: '#2a2521',
    modalBg: '#1a1714',
    canvas: '#121110',
    rootBg: '#121110',
    panelSolid: '#221e1a',
    fallbackWash: '#2a2521',
    fallbackScrim: 'rgba(18,17,16,0.55)',
    warmTrack: '#221e1a',
    neutralSurface: '#2f2925',
    ghostSurface: '#221e1a',
    mutedSurface: '#2f2925',
    pastSurface: '#2a2521',

    hairline: '#3a332c',
    warmLine: 'rgba(214,190,150,0.16)',
    checkboxLine: '#4c443b',
    emptyIcon: '#4c443b',

    ink: '#ede8e1',
    mutedInk: '#b3aba0',
    title: '#e7e1d8',
    chipInk: '#e7e1d8',
    controlInk: '#e7e1d8',
    meta: '#b3aba0',
    faintInk: '#a09789',
    warmMeta: '#b3aba0',
    year: '#a09789',
    pastInk: '#a09789',
    pastMeta: '#8e8579',
    pastFaint: '#8e8579',
    chevronInk: '#a09789',
    onPhoto: 'white',
    onAccent: '#121110',
    onAccentBody: 'rgba(18,17,16,0.82)',
    onAccentFaint: 'rgba(18,17,16,0.76)',
    onInk: '#121110',
    onAccentTile: 'rgba(18,17,16,0.13)',
    onAccentTileLine: 'rgba(18,17,16,0.2)',
    onAccentPanel: 'rgba(18,17,16,0.12)',
    onAccentPanelLine: 'rgba(18,17,16,0.18)',
    photoPlate82: 'rgba(255,255,255,0.94)',
    photoPlate92: 'rgba(255,255,255,0.96)',

    // `markerHalo` is deliberately the dark map's own LAND colour (MAP_DARK_LAND below): its job
    // is to separate a category-hued dot or label from the ground it sits on, which is what white
    // does in light. `markerBg` is the 28px icon disc and must sit a step ABOVE the land or the
    // disc disappears into it — in light the same disc is white on Google's light raster.
    markerHalo: '#1a1714',
    markerBg: '#2f2925',
    markerIconOnFav: 'rgba(255,255,255,0.9)',

    errorBg: '#1a1714',
    errorInk: '#ff8a80',
    errorSoftBg: '#3a1614',
    errorSoftLine: '#5c2420',
    errorSoftInk: '#ffb4ad',
    removeBg: '#3a1614',
    removeLine: 'rgba(255,138,128,0.24)',
    removeInk: '#ffb4ad',
    clearFilterBg: '#3a1614',
    clearFilterInk: '#ff8a80',
    warnInk: '#e0b06a',
    warnSoftBg: '#33240f',
    warnSoftLine: '#4d3717',
    warnSoftInk: '#f0c46a',
    noticeSoftBg: '#3a2a10',
    noticeSoftLine: '#54401a',
    noticeSoftInk: '#f0c46a',

    mapLoadingBg: '#1a1f1c',
    mapPlaceholderBg: '#152019',
    mapPlaceholderInk: '#8fdcaa',
    mapControlLine: 'rgba(95,196,146,0.32)',
    mapPillLine: 'rgba(95,196,146,0.38)',
    geoDot: '#7fb3ff',
    geoActiveInk: '#7fb3ff',
    geoHalo: 'rgba(127,179,255,0.5)',
    geoPulse: 'rgba(127,179,255,0.4)',
    geoPulseFade: 'rgba(127,179,255,0)',
    geoActiveBg: 'rgba(30,52,84,0.72)',
    homePulse: 'rgba(95,196,146,0.4)',
    homePulseFade: 'rgba(95,196,146,0)',

    mapGlass24: 'rgba(18,17,16,0.7)',
    mapGlass28: 'rgba(18,17,16,0.72)',
    mapGlass38: 'rgba(18,17,16,0.74)',
    mapGlass48: 'rgba(18,17,16,0.78)',
    mapGlass52: 'rgba(18,17,16,0.8)',
    mapGlassLine: 'rgba(237,232,225,0.16)',
    mapChipLine: 'rgba(237,232,225,0.26)',
    mapNavBg: 'rgba(18,17,16,0.72)',
    mapNavLine: 'rgba(237,232,225,0.14)',

    navActiveBg: 'rgba(95,196,146,0.22)',
    navActiveLine: 'rgba(95,196,146,0.3)',
    navPastBg: 'rgba(224,176,106,0.18)',
    navPastLine: 'rgba(224,176,106,0.32)',
    navMapInk: 'rgba(237,232,225,0.72)',

    linkButton: '#9cc3f7',
    linkShadow: 'rgba(0,0,0,0.4)',
    mapsLinkBg: 'rgba(34,30,26,0.88)',
    hookWash: 'rgba(16,46,37,0.96)',
    hookLine: 'rgba(95,196,146,0.28)',
    focusRing: 'rgba(95,196,146,0.72)',

    accent07: 'rgba(95,196,146,0.07)',
    accent09: 'rgba(95,196,146,0.09)',
    accent10: 'rgba(95,196,146,0.1)',
    accent14: 'rgba(95,196,146,0.14)',
    accent18: 'rgba(95,196,146,0.18)',
    accent20: 'rgba(95,196,146,0.2)',
    accent24: 'rgba(95,196,146,0.24)',
    accentBright08: 'rgba(127,211,176,0.08)',
    accentBright92: 'rgba(127,211,176,0.92)',

    warm10: 'rgba(214,190,150,0.1)',
    warm12: 'rgba(214,190,150,0.12)',
    warm13: 'rgba(214,190,150,0.13)',
    warm14: 'rgba(214,190,150,0.14)',

    shade06: 'rgba(0,0,0,0.3)',
    shade08: 'rgba(0,0,0,0.38)',
    shade09: 'rgba(0,0,0,0.42)',
    shade10: 'rgba(0,0,0,0.46)',
    shade12: 'rgba(0,0,0,0.5)',
    glassActive: 'rgba(237,232,225,0.76)',

    scrim08: 'rgba(0,0,0,0.28)',
    scrim10: 'rgba(0,0,0,0.34)',
    scrim11: 'rgba(0,0,0,0.36)',
    scrim12: 'rgba(0,0,0,0.4)',
    scrim15: 'rgba(0,0,0,0.46)',
    scrim25: 'rgba(0,0,0,0.55)',
    scrim30: 'rgba(0,0,0,0.6)',
    scrim70: 'rgba(0,0,0,0.82)',

    heroScrim06: 'rgba(10,9,8,0.2)',
    heroScrim30: 'rgba(10,9,8,0.5)',
    heroScrim62: 'rgba(10,9,8,0.78)',
    photoWash: 'rgba(10,9,8,0.72)',

    glass12: 'rgba(28,24,20,0.55)',
    glass13: 'rgba(28,24,20,0.55)',
    glass14: 'rgba(28,24,20,0.6)',
    glass18: 'rgba(28,24,20,0.62)',
    glass20: 'rgba(28,24,20,0.64)',
    glass24: 'rgba(28,24,20,0.66)',
    glass28: 'rgba(28,24,20,0.68)',
    glass38: 'rgba(28,24,20,0.72)',
    glass40: 'rgba(237,232,225,0.4)',
    glass48: 'rgba(28,24,20,0.76)',
    glass52: 'rgba(28,24,20,0.78)',
    glass60: 'rgba(237,232,225,0.6)',
    glass68: 'rgba(28,24,20,0.82)',
    glass72: 'rgba(237,232,225,0.24)',
    glass76: 'rgba(28,24,20,0.86)',
    glass80: 'rgba(237,232,225,0.8)',
    glass82: 'rgba(28,24,20,0.88)',
    glass86: 'rgba(28,24,20,0.9)',
    glass90: 'rgba(34,30,26,0.9)',
    glass92: 'rgba(34,30,26,0.92)',
    glass94: 'rgba(26,23,20,0.94)',
    glass95: 'rgba(34,30,26,0.95)',

    categoryFallbackHue: '#10b981',
    categoryFallbackBg: '#f3f4f6',
    categoryFallbackMuted: 'rgba(16,185,129,0.65)',

    // Dark keeps the same four jobs. `ink` is a light step of the hue, `chipBg` a dark step of it,
    // and `onPlate` is LIGHT's ink, because the plate under it is white in both modes. Four of the
    // eight deliberately reuse the `eventCat` dark values for the same hue family — shop/festival,
    // nature/nature, hotel/cultural, religious/religious — so one purple, one green, one blue and
    // one amber are used across the app rather than two of each.
    categoryInk: {
      restaurant: { ink: '#f2a9a0', chipBg: '#3a1c18', chipLine: '#f2a9a022', onPlate: '#b91c1c' },
      cafe: { ink: '#f0b183', chipBg: '#3a2410', chipLine: '#f0b18322', onPlate: '#c2410c' },
      shop: { ink: '#d6b4f7', chipBg: '#2d1f3d', chipLine: '#d6b4f722', onPlate: '#7c3aed' },
      heritage: { ink: '#cfc4b6', chipBg: '#332d28', chipLine: '#cfc4b622', onPlate: '#57534e' },
      nature: { ink: '#8fdcaa', chipBg: '#10301c', chipLine: '#8fdcaa22', onPlate: '#166534' },
      hotel: { ink: '#9cc3f7', chipBg: '#14243d', chipLine: '#9cc3f722', onPlate: '#1d4ed8' },
      religious: { ink: '#f0c46a', chipBg: '#3a2a10', chipLine: '#f0c46a22', onPlate: '#92400e' },
      health: { ink: '#7fd0c6', chipBg: '#0f2e2b', chipLine: '#7fd0c622', onPlate: '#0f766e' },
      fallback: { ink: '#b3aba0', chipBg: '#2a2521', chipLine: '#b3aba022', onPlate: '#4B5563' },
    },
    categoryOnFill: '#121110',

    shadowAction: '0 12px 24px rgba(15,81,50,0.18)',
    shadowCard: '0 20px 46px rgba(31,41,55,0.1)',
    shadowMapControl: '0 2px 10px rgba(0,0,0,0.15)',
    shadowSoft: '0 12px 30px rgba(31,41,55,0.08)',
    shadowPrimaryBtn: '0 10px 22px rgba(15,81,50,0.16)',

    eventTodayFill: '#5fc492',
    eventTodayInk: '#121110',
    eventDaySpine: '#4c443b',
    eventRunTrack: 'rgba(95,196,146,0.34)',
    eventWeekendPlate: '#2a2521',
    eventWeekendPlateLine: 'rgba(214,190,150,0.26)',

    cazaBadge: {
      zgharta: { color: '#7fd3b0', background: '#102e25' },
      bsharri: { color: '#e0b06a', background: '#33240f' },
      neutral: { color: '#b3aba0', background: '#2a2521' },
    },
    cazaArea: {
      zgharta: '#5fc492',
      bsharri: '#d9a35a',
    },

    eventCat: {
      festival: { bg: '#2d1f3d', color: '#d6b4f7', line: '#d6b4f722' },
      religious: { bg: '#3a2a10', color: '#f0c46a', line: '#f0c46a22' },
      nature: { bg: '#10301c', color: '#8fdcaa', line: '#8fdcaa22' },
      cultural: { bg: '#14243d', color: '#9cc3f7', line: '#9cc3f722' },
    },
  },
};

// --- Google Maps embedded `styles` arrays (#424; research D6) --------------------------------
// These are Maps API OPTIONS, not CSS. `MapOptions.styles` takes literal colours and cannot read
// a `var()`, and the array is not a colour ramp, so it does not belong inside VISITOR_PALETTES —
// which is flattened into one `--visitor-*` custom property per leaf. It lives in this file
// because this file is the one place a colour literal may be written (the hex gate in
// visitorTheme.test.js).
//
// The app loads Maps WITHOUT a map ID and without vector maps, which is exactly the condition
// under which `MapOptions.styles` is available at all ("This feature is not available when using
// a map ID, or when using vector maps") — so dark is a second embedded array swapped at runtime
// with `map.setOptions({ styles })`, never a cloud map ID.

// THE POI RULES ARE PART OF EVERY ARRAY. `setOptions({ styles })` REPLACES the array wholesale —
// it does not merge — so a dark array that forgot them would turn every Google POI, POI business
// and transit station back on the moment a visitor switched to dark (#421 D6). They are appended
// LAST in both arrays, so no later rule can raise a hidden feature back to visible.
const MAP_POI_HIDDEN = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit.station', stylers: [{ visibility: 'off' }] },
];

// The dark map's ground. `VISITOR_PALETTES.dark.markerHalo` is pinned to this value by test: the
// marker halo's whole job is to separate a category-hued dot or label from the land behind it.
const MAP_DARK_LAND = '#1a1714';
// Water is the one cool note in a warm palette, because a warm-brown sea reads as more land.
const MAP_DARK_WATER = '#10161c';

// A RECOLOUR, never a re-selection: every rule here sets `color`, and not one sets `visibility`.
// Dark and light therefore show a visitor the same features — the same roads, the same village
// labels, the same water — and differ only in paint. A `visibility` styler in this array would
// be a second, undocumented answer to "what is on the map", which is the map equivalent of
// status-by-colour (#421 D7).
const MAP_DARK_GEOMETRY = [
  { elementType: 'geometry', stylers: [{ color: MAP_DARK_LAND }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: MAP_DARK_LAND }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9c9084' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#3a332c' }] },
  // Village names are the app's own vocabulary — the Directory, the Guide and the village filter
  // all speak in them — so the locality label is the BRIGHTEST label class on the dark map.
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#cbc0b1' }] },
  { featureType: 'administrative.neighborhood', elementType: 'labels.text.fill', stylers: [{ color: '#b3aba0' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#1d1a15' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2e2823' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#231f1b' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#988d80' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4a4038' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#2b2620' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#cbc0b1' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2a2521' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: MAP_DARK_WATER }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#5b6b78' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: MAP_DARK_WATER }] },
];

export const VISITOR_MAP_DARK_LAND = MAP_DARK_LAND;

// Light is EXACTLY the three POI rules and nothing else: the map keeps Google's default raster
// palette, which is what the app has always shipped. A light colour rule added here would move a
// pixel on a screen `tests/e2e/visitor-theme-light-unchanged.mjs` diffs against origin/main.
//
// A fresh array every call, because `Map` keeps the reference it is handed.
export function visitorMapStyles(mode) {
  return mode === 'dark'
    ? [...MAP_DARK_GEOMETRY, ...MAP_POI_HIDDEN]
    : [...MAP_POI_HIDDEN];
}

// `{ a: 1, b: { c: 2 } }` -> `[['a', 1], ['b-c', 2]]`. The joiner is `-` so the emitted custom
// property reads `--visitor-cazaBadge-zgharta-color`.
function flattenPalette(palette, prefix = '') {
  return Object.keys(palette).reduce((entries, key) => {
    const value = palette[key];
    const name = prefix ? `${prefix}-${key}` : key;
    if (value && typeof value === 'object') return entries.concat(flattenPalette(value, name));
    entries.push([name, value]);
    return entries;
  }, []);
}

// The same shape as a palette, with every leaf replaced by the `var()` that reads it.
function varsForShape(palette, prefix = '') {
  return Object.keys(palette).reduce((shape, key) => {
    const value = palette[key];
    const name = prefix ? `${prefix}-${key}` : key;
    shape[key] = value && typeof value === 'object'
      ? varsForShape(value, name)
      : `var(--visitor-${name})`;
    return shape;
  }, {});
}

export const visitorTheme = varsForShape(VISITOR_PALETTES.light);

export function visitorThemeCssVariables(mode) {
  const palette = VISITOR_PALETTES[mode] || VISITOR_PALETTES.light;
  const vars = {};
  flattenPalette(palette).forEach(([name, value]) => {
    vars[`--visitor-${name}`] = value;
  });
  return vars;
}

export function resolveVisitorTheme({ stored, systemDark } = {}) {
  if (stored === 'light' || stored === 'dark') return stored;
  return systemDark ? 'dark' : 'light';
}

// `window.localStorage` is a GETTER that itself throws SecurityError when site data is blocked
// (sandboxed origin, Safari with cookies off) — before any getItem/setItem call. Reading the
// property at the call site and passing the result in therefore throws OUTSIDE the guards below.
// On the admin side that crashed the whole dashboard shell during render (cold review, #416);
// here it would take the visitor app down. Every access goes through this.
export function visitorThemeStorage() {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
}

export function readStoredVisitorTheme(storage) {
  try {
    if (!storage || typeof storage.getItem !== 'function') return null;
    const value = storage.getItem(VISITOR_THEME_STORAGE_KEY);
    if (value === 'light' || value === 'dark') return value;
    return null;
  } catch (error) {
    return null;
  }
}

export function writeStoredVisitorTheme(storage, mode) {
  try {
    if (!storage) return;
    if (mode === null || mode === undefined) {
      if (typeof storage.removeItem === 'function') storage.removeItem(VISITOR_THEME_STORAGE_KEY);
      return;
    }
    if ((mode === 'light' || mode === 'dark') && typeof storage.setItem === 'function') {
      storage.setItem(VISITOR_THEME_STORAGE_KEY, mode);
    }
  } catch (error) {
    // Safari private mode / blocked site data — persistence is optional.
  }
}
